import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Card, Typography, Button, TextField, Grid, Chip,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Alert, Table, TableBody,
  TableCell, TableHead, TableRow, Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import api from "../../utils/api";

const EMPTY = { FirstName:"", LastName:"", VehiclePlate:"", Reason:"" };

const GateBlacklistPage = () => {
  const [list, setList]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm]       = useState(EMPTY);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");
  const [snack, setSnack]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/gate-blacklist");
      setList(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.FirstName && !form.LastName && !form.VehiclePlate) {
      setError("Provide at least a name or vehicle plate."); return;
    }
    setSaving(true); setError("");
    try {
      await api.post("/gate-blacklist", form);
      setAddOpen(false); setForm(EMPTY);
      setSnack("Added to blacklist."); load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add.");
    } finally { setSaving(false); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("Remove from blacklist?")) return;
    try {
      await api.patch(`/gate-blacklist/${id}/deactivate`);
      setSnack("Removed from blacklist."); load();
    } catch (err) { alert("Failed."); }
  };

  const fmtDT = (d) => d ? new Date(d).toLocaleString() : "N/A";
  const cardSx = { borderRadius:"16px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.06)", p:2.5 };
  const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px", backgroundColor:"rgba(255,255,255,0.96)" } };
  const btnSx = { borderRadius:"10px", textTransform:"none", fontWeight:700, boxShadow:"none",
    backgroundColor:"#dc2626", "&:hover":{ backgroundColor:"#b91c1c", boxShadow:"none" } };

  return (
    <Box>
      {snack && <Alert severity="success" onClose={()=>setSnack("")} sx={{ mb:2, borderRadius:2 }}>{snack}</Alert>}

      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight:800, color:"#223142" }}>Blacklist Registry</Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>
            Gate staff is warned when a blacklisted name or plate is entered.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} sx={btnSx} onClick={()=>{ setAddOpen(true); setError(""); }}>
          Add to Blacklist
        </Button>
      </Box>

      {loading ? <CircularProgress /> : (
        <Card sx={cardSx}>
          {list.length === 0 ? (
            <Typography sx={{ color:"#6b778c", textAlign:"center", py:4 }}>No blacklist entries.</Typography>
          ) : (
            <Paper elevation={0} sx={{ borderRadius:"10px", overflow:"hidden", border:"1px solid rgba(47,99,173,0.08)" }}>
              <Table size="small">
                <TableHead sx={{ bgcolor:"rgba(220,38,38,0.04)" }}>
                  <TableRow>
                    {["Name","Vehicle Plate","Reason","Added By","Date","Status",""].map(h=>(
                      <TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map((b) => (
                    <TableRow key={b.BlacklistID} hover sx={{ opacity: b.IsActive ? 1 : 0.5 }}>
                      <TableCell sx={{ fontWeight:600, color:"#223142" }}>{[b.FirstName,b.LastName].filter(Boolean).join(" ") || "N/A"}</TableCell>
                      <TableCell sx={{ color:"#6b778c", fontSize:12 }}>{b.VehiclePlate || "N/A"}</TableCell>
                      <TableCell sx={{ color:"#6b778c", fontSize:12, maxWidth:200 }}>{b.Reason || "N/A"}</TableCell>
                      <TableCell sx={{ color:"#6b778c", fontSize:12 }}>{b.AddedBy || "N/A"}</TableCell>
                      <TableCell sx={{ color:"#6b778c", fontSize:12 }}>{fmtDT(b.AddedAt)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={b.IsActive ? "Active" : "Removed"}
                          sx={{ fontWeight:600, fontSize:11, borderRadius:"8px",
                            bgcolor: b.IsActive ? "rgba(220,38,38,0.10)" : "rgba(0,0,0,0.06)",
                            color:   b.IsActive ? "#dc2626" : "#6b778c" }} />
                      </TableCell>
                      <TableCell>
                        {b.IsActive && (
                          <Tooltip title="Remove from Blacklist">
                            <IconButton size="small" color="success" onClick={()=>handleRemove(b.BlacklistID)}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Card>
      )}

      <Dialog open={addOpen} onClose={()=>setAddOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:"16px" } }}>
        <DialogTitle sx={{ fontWeight:700, color:"#dc2626" }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:1 }}><BlockIcon />Add to Blacklist</Box>
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb:2, borderRadius:2 }}>{error}</Alert>}
          <Alert severity="warning" sx={{ mb:2, borderRadius:2 }}>
            Gate staff will be warned when this name or plate is entered. Provide at least one identifier.
          </Alert>
          <Grid container spacing={2} sx={{ mt:0.5 }}>
            <Grid item xs={6}>
              <TextField name="FirstName" label="First Name" fullWidth size="small"
                value={form.FirstName} onChange={e=>setForm(f=>({...f,FirstName:e.target.value}))} sx={fieldSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField name="LastName" label="Last Name" fullWidth size="small"
                value={form.LastName} onChange={e=>setForm(f=>({...f,LastName:e.target.value}))} sx={fieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField name="VehiclePlate" label="Vehicle Plate" fullWidth size="small"
                value={form.VehiclePlate} onChange={e=>setForm(f=>({...f,VehiclePlate:e.target.value}))} sx={fieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField name="Reason" label="Reason" fullWidth multiline rows={2} size="small"
                value={form.Reason} onChange={e=>setForm(f=>({...f,Reason:e.target.value}))} sx={fieldSx} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:3, gap:1 }}>
          <Button onClick={()=>setAddOpen(false)} variant="outlined"
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained" disabled={saving} sx={btnSx}>
            {saving ? <CircularProgress size={20} color="inherit" /> : "Add to Blacklist"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GateBlacklistPage;
